<template>
  <button
    class="fp-consent-button"
    type="button"
    @click="openConsentPreferences"
  >
    <span class="fp-consent-button__icon"></span>
    <span class="fp-consent-button__label">{{ $t('header.manage_cookies') }}</span>
  </button>
</template>

<script>
import Consent from '@/shared/Consent'

/*
 * The cookie-consent entry point, at the bottom of every page. Mounted once, in
 * src/shared/RouteWrapper.vue, so every routed page of every SPA gets it — the header has
 * no cookie button anymore.
 *
 * Discreet on purpose: a translucent circle that expands to the right on hover to reveal
 * its label. The label stays in the DOM (only clipped) so it remains the accessible name.
 */
export default {
  name: 'FpConsentButton',
  methods: {
    openConsentPreferences () {
      Consent.showPreferences()
    }
  }
}
</script>

<style lang="less" scoped>
@import "~@/shared/styles/_variables.less";

.fp-consent-button {
  position: fixed;
  left: 20px;
  bottom: 20px;
  // Above the modals (2000 in modals.less): consent must stay reachable during the
  // full-screen flows, project creation for one
  z-index: 2100;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  // 9px + 22px icon + 9px keeps it a 40px circle until the label expands
  height: 40px;
  padding: 0 9px;
  overflow: hidden;
  white-space: nowrap;
  // Bare icon at rest; the pill background only appears on hover. The transparent border
  // is kept so gaining the visible one on hover does not shift the layout.
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 20px;
  cursor: pointer;
  opacity: 0.65;
  box-shadow: none;
  transition: opacity 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    opacity: 1;
    background-color: @white;
    border-color: fade(@grey, 60%);
    box-shadow: 0 3px 12px 0 fade(@grey, 50%);

    .fp-consent-button__label {
      max-width: 160px;
      margin-left: 8px;
    }

    @media (prefers-reduced-motion: no-preference) {
      .fp-consent-button__icon {
        // Eats the cookie frame by frame (right bite, left bite, crumbs),
        // holds the last frame while hovered, snaps back on mouseleave
        animation: cookie-eat 0.9s forwards;
      }
    }
  }

  // Rendered as a mask (not an <img>) so the hover animation can swap frames
  &__icon {
    display: block;
    flex: none;
    width: 22px;
    height: 22px;
    background-color: @grey;
    mask: url('~@/shared/assets/img/cookie.svg') no-repeat center / contain;
    -webkit-mask: url('~@/shared/assets/img/cookie.svg') no-repeat center / contain;
  }

  &__label {
    max-width: 0;
    margin-left: 0;
    overflow: hidden;
    font-size: 13px;
    line-height: 1;
    color: @text-color;
    transition: max-width 0.25s ease, margin-left 0.25s ease;
  }
}

// Frame-by-frame "cookie being eaten" mask swap for the hover above.
// mask-image is not interpolable, so each keyframe pair pins a discrete frame;
// the flips land between the paired stops (29->30%, 59->60%, 89->90%).
@keyframes cookie-eat {
  0%, 29% {
    mask-image: url('~@/shared/assets/img/cookie.svg');
    -webkit-mask-image: url('~@/shared/assets/img/cookie.svg');
  }
  30%, 59% {
    mask-image: url('~@/shared/assets/img/cookie-bitten.svg');
    -webkit-mask-image: url('~@/shared/assets/img/cookie-bitten.svg');
  }
  60%, 89% {
    mask-image: url('~@/shared/assets/img/cookie-eaten.svg');
    -webkit-mask-image: url('~@/shared/assets/img/cookie-eaten.svg');
  }
  90%, 100% {
    mask-image: url('~@/shared/assets/img/cookie-crumbs.svg');
    -webkit-mask-image: url('~@/shared/assets/img/cookie-crumbs.svg');
  }
}
</style>
